import { Component, inject, OnInit, ChangeDetectorRef, NgZone, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ProfileService } from '../../../core/services/profile.service';
import { ProfileListing } from '../../../core/models/announcement.model';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
  selector: 'app-my-ads',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './my-ads.component.html',
  styleUrl: './my-ads.component.scss'
})
export class MyAdsComponent implements OnInit {
  private profileService = inject(ProfileService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  activeTab = 'Aktiv';
  viewMode: 'grid' | 'list' = 'grid';
  allAds: ProfileListing[] = [];
  filteredAds: ProfileListing[] = [];
  isLoading = true;
  showPauseModal = false;
  showRestoreModal = false;
  selectedAdId: number | null = null;

  tabs = ['Aktiv', 'Gözləyir', 'Rədd edilmiş', 'Dayandırılmış'];

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const tabParam = this.route.snapshot.queryParamMap.get('tab');
      if (tabParam === 'pending') {
        this.activeTab = 'Gözləyir';
      }
      this.loadAds();
    }
  }

  loadAds(): void {
    this.isLoading = true;
    this.profileService.getListings().subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          if (response && response.success) {
            this.allAds = response.data || [];
            this.filterAds();
          } else {
            this.allAds = [];
            this.filteredAds = [];
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.isLoading = false;
          this.allAds = [];
          this.filteredAds = [];
          this.cdr.detectChanges();
        });
      }
    });
  }

  filterAds(): void {
    // API-dan gələn statusları tablara uyğunlaşdırırıq
    // listingModerationStatus: 1 (Pending), 2 (Active), 3 (Rejected), 4 (Expired)
    // profileDisplayStatus: 0 (Visible/Active), 2 (Hidden/Paused)

    this.filteredAds = this.allAds.filter(ad => {
      // Dayandırılmış hər zaman üstünlük təşkil edir
      // İstifadəçi qeyd edir ki, dayandırılmış elanın statusu 4-dür
      const isPaused = ad.profileDisplayStatus === 2 || ad.listingModerationStatus === 4;

      if (this.activeTab === 'Dayandırılmış') {
        return isPaused;
      }

      // Əgər elan dayandırılıbsa, digər tablarda (Aktiv, Gözləyir və s.) görünməməlidir
      if (isPaused) {
        return false;
      }

      switch (this.activeTab) {
        case 'Aktiv':
          return ad.listingModerationStatus === 2;
        case 'Gözləyir':
          return ad.listingModerationStatus === 1;
        case 'Rədd edilmiş':
          return ad.listingModerationStatus === 3;
        default:
          return true;
      }
    });

    // Angular-ın change detection mexanizmini məcburi tetiklemek üçün
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.filterAds();
  }

  createNewAd(): void {
    this.router.navigate(['/announcements/new']);
  }

  viewAd(id: number): void {
    this.router.navigate(['/listings', id]);
  }

  editAd(id: number): void {
    this.router.navigate(['/announcements/edit', id]);
  }

  confirmPause(id: number): void {
    this.selectedAdId = id;
    this.showPauseModal = true;
  }

  confirmRestore(id: number): void {
    this.selectedAdId = id;
    this.showRestoreModal = true;
  }

  closeModal(): void {
    this.showPauseModal = false;
    this.showRestoreModal = false;
    this.selectedAdId = null;
  }

  pauseAd(): void {
    if (this.selectedAdId === null) return;

    const id = this.selectedAdId;
    this.profileService.pauseListing(id).subscribe({
      next: (res) => {
        if (res.success) {
          this.alertService.success('Elan dayandırıldı');
          this.loadAds();
          this.closeModal();
        } else {
          this.alertService.error(res.message || 'Xəta baş verdi');
        }
      },
      error: (err) => {
        this.alertService.error('Sistem xətası baş verdi');
      }
    });
  }

  restoreAd(): void {
    if (this.selectedAdId === null) return;

    const id = this.selectedAdId;
    this.profileService.activateListing(id).subscribe({
      next: (res) => {
        if (res.success) {
          this.alertService.success('Elan bərpa edildi');
          this.loadAds();
          this.closeModal();
        } else {
          this.alertService.error(res.message || 'Xəta baş verdi');
        }
      },
      error: (err) => {
        this.alertService.error('Sistem xətası baş verdi');
      }
    });
  }

  isAdPaused(ad: ProfileListing): boolean {
    return ad.profileDisplayStatus === 2 || ad.listingModerationStatus === 4;
  }

  getStatusLabel(ad: ProfileListing): string {
    if (this.isAdPaused(ad)) return 'Dayandırılmış';
    switch (ad.listingModerationStatus) {
      case 1: return 'Gözləyir';
      case 2: return 'Aktiv';
      case 3: return 'Rədd edilmiş';
      default: return 'Naməlum';
    }
  }

  getStatusClass(ad: ProfileListing): string {
    if (this.isAdPaused(ad)) return 'status-paused';
    switch (ad.listingModerationStatus) {
      case 1: return 'status-pending';
      case 2: return 'status-active';
      case 3: return 'status-rejected';
      default: return '';
    }
  }
}
